import './CommerceOps.scss';
import { ReactElement, useCallback, useMemo } from 'react';
import { OverviewToplineCoreField } from '@blisspointmedia/bpm-types/dist/CommerceOps';
import { VOLUME_FORMATTER } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { SparkChart } from '@Components/SparkChart/SparkChart';
import { CommerceOpsTable } from './CommerceOpsTable';
import {
  AsinDimension,
  constructMetricFormatter,
  OVERVIEW_BIGGEST_MOVERS_DIMENSION_CONFIG_MAP,
  OVERVIEW_METRIC_TO_FORMATTER,
  OVERVIEW_TOP_ASINS_DIMENSION_CONFIG_MAP,
  OVERVIEW_TOPLINE_METADATA_MAP,
  OverviewTabProps
} from './CommerceOpsUtils';

const DEFAULT_SPARK_CHART_FIELDS = [
  OverviewToplineCoreField.TOTAL_UNITS_SOLD,
  OverviewToplineCoreField.TOTAL_SALES,
  OverviewToplineCoreField.PAID_SALES,
  OverviewToplineCoreField.TROAS
];

const Overview = ({
  data: { primaryTimeSeries, compTimeSeries, performanceData, asinEntries },
  downloadTable,
  config: { sparkChartOptions, performanceConfigMap }
}: OverviewTabProps): ReactElement => {
  const performanceDataMap = useMemo(
    () =>
      Object.fromEntries(Object.keys(performanceConfigMap).map(dim => [dim, performanceData[dim]])),
    [performanceData, performanceConfigMap]
  );

  const asinDataMap = useMemo(
    () => ({
      [AsinDimension.ASIN]: asinEntries
    }),
    [asinEntries]
  );

  const downloadPerformanceCsv = useCallback(
    () => downloadTable('performance', performanceDataMap),
    [performanceDataMap, downloadTable]
  );

  const downloadAsinCsv = useCallback(
    () => downloadTable('asin', asinDataMap),
    [asinDataMap, downloadTable]
  );

  return (
    <>
      <div className='commerce-ops-section'>
        {DEFAULT_SPARK_CHART_FIELDS.map(field => (
          <SparkChart
            key={field}
            data={primaryTimeSeries}
            otherData={compTimeSeries}
            defaultMetric={field}
            metricOptions={sparkChartOptions}
            invertedMetrics={[OverviewToplineCoreField.TACOS]}
            columnMetaDataMap={OVERVIEW_TOPLINE_METADATA_MAP}
            formatter={constructMetricFormatter(OVERVIEW_METRIC_TO_FORMATTER, VOLUME_FORMATTER)}
          />
        ))}
      </div>
      <div className='commerce-ops-section'>
        <CommerceOpsTable
          key={Object.keys(performanceConfigMap).join(',')}
          topRowConfig={{ showTopRow: true, dropdownEnabled: true }}
          dimensionConfigMap={performanceConfigMap}
          dataMap={performanceDataMap}
          onDownload={downloadPerformanceCsv}
        />
      </div>
      <div className='commerce-ops-section two-tables'>
        <CommerceOpsTable
          topRowConfig={{ showTopRow: true, searchBelowHeader: true, dropdownEnabled: false }}
          dimensionConfigMap={OVERVIEW_TOP_ASINS_DIMENSION_CONFIG_MAP}
          dataMap={asinDataMap}
          onDownload={downloadAsinCsv}
        />
        <CommerceOpsTable
          topRowConfig={{ showTopRow: true, searchBelowHeader: true, dropdownEnabled: false }}
          dimensionConfigMap={OVERVIEW_BIGGEST_MOVERS_DIMENSION_CONFIG_MAP}
          dataMap={asinDataMap}
          onDownload={downloadAsinCsv}
        />
      </div>
    </>
  );
};

export { Overview };
